import { Info } from "lucide-react";
import { usePageGuidance } from "@/contexts/pageGuidanceContextValue";

/**
 * The inline "what this section is for" line (BEN-141 page guidance, slice 1).
 *
 * TREATMENT — inline, always visible, no dismiss. This follows S10's conclusion rather
 * than the mock's drawing, for two reasons S10 established the hard way:
 *
 *  1. Tap-to-reveal loses on touch. S10 found the page-level explanation was a Radix
 *     tooltip, which opens on pointermove only when pointerType !== "touch" — so at
 *     390px the string was not merely awkward to reach, it was never in the DOM. Its fix
 *     was "inline-below-the-title over tap-to-reveal… it costs one paragraph and no
 *     interaction machinery". A section-level chip behind a tap is the same bet S10 lost.
 *
 *  2. The mock's dismiss (×) implies per-chip persistence, and there is none. Seven
 *     chips on this page alone, and nothing anywhere stores "this user closed this
 *     explanation". Shipping the × without a store gives a control that forgets on every
 *     navigation — the class of defect this campaign has been removing, not adding. So
 *     there is no ×. If dismissal is wanted it needs a store first, and that is a
 *     separate slice with a real cost, not a styling detail.
 *
 * WHY A TINT rather than a third muted paragraph. Each card already stacks a heading and
 * a data-basis line ("Averaged over the last 11 complete months"). A third 12px muted
 * paragraph directly under those reads as another caveat about the numbers, which is the
 * one thing this is not. The tint plus the icon is the cheapest way to mark a change of
 * register; it stays inside the card, so it is not a banner (CLAUDE.md: blend alerts into
 * existing layouts, differentiate with accent-coloured icons).
 *
 * The icon is decorative — the sentence carries the whole meaning, so a screen reader
 * that announced "info" first would only add noise.
 */
interface SectionExplainerProps {
  /** Anthony's approved sentence. Pass from SECTION_EXPLAINERS — never a literal. */
  text: string;
}

export function SectionExplainer({ text }: SectionExplainerProps) {
  const guidance = usePageGuidance();
  if (guidance.provided && !guidance.enabled) return null;

  return (
    <p
      // Structural hook, following the repo's existing `data-slot` convention (card,
      // dialog-title, …). It exists so a test can COUNT explainers in a card rather than
      // search for known strings: the string-based guard only catches copy already in
      // SECTION_EXPLAINERS, so a section that gained a brand-new sentence would slip past
      // it. Counting catches any explainer, approved or not.
      data-slot="section-explainer"
      className="flex items-start gap-1.5 rounded-lg bg-brand-accent/5 px-2.5 py-2 text-[12px] leading-snug text-muted-foreground"
    >
      <Info
        className="mt-[1px] h-3.5 w-3.5 flex-shrink-0 text-brand-accent"
        strokeWidth={2}
        aria-hidden="true"
      />
      <span>{text}</span>
    </p>
  );
}
